import { useState } from "react";
import { Clock, Share2, ChevronRight, MapPin, ExternalLink, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Article 1 hero: aerial Brasov old town at golden hour (Varianta A)
const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
// Article 1 in-article: Piata Sfatului spring terrace (Varianta B)
// In-article image: hiking trail on Tampa mountain (moved from Article 2)
const STREET_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article1-tampa-trail-3TGGR5NYsVYgqEFmkoRyyn.webp";
const REALESTATE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp";

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania Brașov" className="w-full h-48 object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function InArticleBanner() {
  return (
    <div className="my-10 border border-stone-200 overflow-hidden">
      <div className="px-4 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col sm:flex-row">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full sm:w-48 h-36 object-cover flex-shrink-0" />
        <div className="p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
              Apartments for sale in Brașov
            </p>
            <h4 className="font-bold text-stone-900 mb-2 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.05rem" }}>
              Cartier Transilvania — 208 apartments in Noua
            </h4>
            <p className="text-stone-500 text-sm font-light leading-relaxed">
              T1, T2, T3 completed and occupied. T4, T5, T6 under construction — delivery September 2027. Solar panels &amp; heat pump heating.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <a
              href="https://www.cartiertransilvania.ro/en/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-white px-4 py-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
              VISIT PROJECT SITE <ChevronRight size={11} />
            </a>
            <span className="text-xs text-stone-400">cartiertransilvania.ro</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Cartier Transilvania" className="w-full md:w-56 h-40 object-cover flex-shrink-0" />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

const ARTICLE2_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-entertainment-hero-YwxKL9nvfxZqDmSm9YR8ym.webp";
const ARTICLE6_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp";

const RELATED = [
  { tag: "City Guide", title: "What to do in Brașov: a guide for people who are tired of tourist guides", img: ARTICLE2_IMG, href: "/what-to-do-in-brasov-city-guide" },
  { tag: "Real Estate", title: "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing", img: ARTICLE6_IMG, href: "/cartier-transilvania-brasov" },
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: the honest numbers", img: ARTICLE4_IMG, href: "/cost-of-living-brasov" },
];

function NewsletterInline() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch { setStatus("error"); }
  };
  if (status === "success") return <p className="text-xs text-green-400 py-2">✓ Subscribed! See you Friday.</p>;
  return (
    <form onSubmit={handleSubmit}>
      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="w-full bg-white/10 border border-white/20 text-white text-xs px-3 py-2.5 mb-3 outline-none focus:border-white/40 placeholder:text-stone-600" />
      <button type="submit" disabled={status === "loading"} className="w-full text-xs text-white py-2.5 transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-400 mt-1">Try again.</p>}
    </form>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function Article() {
  return (
    <>
    <Helmet>
      <title>Moving to Brașov, Romania: A Foreigner's Honest Guide to the City</title>
      <meta name="description" content="Thinking about moving to Brașov, Romania? An honest guide for expats — why people choose it over Bucharest, what life actually looks like, and what to expect." />
      <meta name="keywords" content="moving to Brasov Romania, expats Brasov, relocate to Brasov, living in Brasov Romania, why move to Brasov" />
      <meta property="og:title" content="Moving to Brașov, Romania: A Foreigner's Honest Guide to the City" />
      <meta property="og:description" content="Thinking about moving to Brașov, Romania? An honest guide for expats — why people choose it over Bucharest, what life actually looks like, and what to expect." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://brasovgateway.com/why-expats-choose-brasov" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Moving to Brașov, Romania: A Foreigner's Honest Guide to the City" />
      <link rel="canonical" href="https://brasovgateway.com/why-expats-choose-brasov" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": "https://brasovgateway.com/why-expats-choose-brasov#article",
              "headline": "Brașov, straight up: beyond the bear photos and the mountain air clichés",
              "description": "A no-nonsense introduction to Brașov, Romania — for those thinking about moving their life and money there.",
              "image": "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp",
              "datePublished": "2026-04-01",
              "dateModified": "2026-04-29",
              "author": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Brașov Gateway",
                "url": "https://brasovgateway.com"
              },
              "url": "https://brasovgateway.com/why-expats-choose-brasov",
              "inLanguage": "en",
              "articleSection": "City Guide"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://brasovgateway.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://brasovgateway.com/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Brașov, straight up: beyond the bear photos and the mountain...",
                  "item": "https://brasovgateway.com/why-expats-choose-brasov"
                }
              ]
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      <div className="relative h-72 md:h-96 mt-16">
        <img src={HERO_IMG} alt="Aerial view of Brașov old town — moving to Brașov Romania guide" className="w-full h-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-12">
          <main className="flex-1 min-w-0">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                  City guide
                </span>
                <div className="flex items-center gap-1 text-stone-400 text-xs">
                  <Clock size={11} />
                  <span>7 min read</span>
                </div>
              </div>
              <h1 className="text-stone-900 mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700 }}>
                Moving to Brașov, Romania: what it actually looks like beyond the mountain air clichés
              </h1>
              <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                    <img src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp" alt="Brașov Gateway" className="w-full h-full object-cover opacity-50" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                    <p className="text-xs text-stone-500">Relocation &amp; Lifestyle Team</p>
                  </div>
                </div>
                <ShareButton url="https://brasovgateway.com/why-expats-choose-brasov" title="Why Move to Brașov? A Foreigner's Honest Guide to Romania's Best City" />
              </div>
            </div>

            <div>
              <p className="text-stone-700 text-lg font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Moving to Brașov, Romania is one of those decisions that looks impulsive from the outside and obvious from the inside. You know that feeling when you arrive somewhere new and everything looks like it was staged for a tourism brochure? Everyone is smiling a little too hard, the coffee costs twice what it should, and you cannot shake the feeling that you are just a walking wallet the locals have been waiting to shake. Well, Brașov is not like that — and <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>the cost of living numbers back that up</Link>. Or at least, it is not only like that.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8" style={{ lineHeight: "1.8" }}>
                If you are thinking about moving your life and your money here, you have probably already heard the tired line about the city at the foot of Tampa Mountain and the crisp mountain air. Skip the poetry. The truth is that Brașov is the kind of place that drives you slightly mad at first - and then makes you never want to leave. Here is <Link href="/what-to-do-in-brasov-city-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>how things actually work</Link>, no Instagram filter applied. If you are coming here to work rather than retire, the 2026 rules for <Link href="/working-in-romania-foreigner-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>working in Romania as a foreigner</Link> have just been overhauled — worth reading before you sign anything.
              </p>
              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The middle of nowhere that is actually the middle of everything
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                Look at a map of Romania and Brașov sits almost dead center, tucked inside a natural fortress of mountains. When I say mountains, I do not mean the kind you hike in loafers on a Sunday afternoon. I mean the Carpathians - the real thing. Which means proper snow in winter and summers that do not melt you into the pavement the way they do in Bucharest or most of Southern Europe.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                That clean mountain air everyone keeps mentioning? It is not a marketing invention. It genuinely exists. But it comes packaged with weather that can change faster than a teenager's mood. You can leave the house in a t-shirt at 9am and be desperately hunting for a jacket by noon. If you need constant sunshine and predictability, you might get restless. But if you like having a forest ten minutes from your front door, you have landed in the right place.
              </p>

              <figure className="my-10">
                <img src={STREET_IMG} alt="Hiking trail on Tampa mountain, Brașov" className="w-full h-72 object-cover" loading="lazy" />
                <figcaption className="text-xs text-stone-400 mt-2 text-center">The Tampa mountain trail — fifteen minutes from the city centre, a world away from it</figcaption>
              </figure>

              <InArticleBanner />

              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Thick walls and trading in the blood
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The history of this place is a strange, layered mix - and it has left deep marks on how the city works today. The Saxons built it, the Hungarians administered it for a while, and the Romanians filled it with life. Walk through the old town and you will find walls that are hundreds of years thick and defensive towers that were not built for show. Those people were not constructing for Instagram. They were building to survive invasions.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                And it survived. Today the invasions are mostly weekend tourists, but the architecture stayed. Brașov has always known how to trade, how to sit at the crossroads between East and West and <Link href="/company-setup-romania" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>make something of it</Link>. That pragmatic, commercial instinct is still in the air. Brașovians do not rush into things - they analyze, they calculate, and then they shake hands. It can feel slow at first. It is not.
              </p>
              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                Where the biggest danger has fur
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                If you are coming from London, Paris, or any major American city, the safety situation here might genuinely surprise you. You can walk around at 2am without checking over your shoulder every thirty seconds or keeping a death grip on your phone. The crime rate is laughably low for a city this size.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                The most dangerous thing you are likely to encounter at night is a bear that has wandered down from the hills looking for leftover pizza near the bins in the Racadau neighborhood. And no, that is not a metaphor. You get used to the idea, the same way you get used to the Friday evening traffic.
              </p>
              <h2 className="text-stone-900 mt-10 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                The slow-motion syndrome
              </h2>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                This is where a lot of expats hit a wall. People in Brașov have their own rhythm. If you are used to the frantic energy of a major capital - where everyone moves like they have left the iron on - things here will seem like they are running at half speed.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-6" style={{ lineHeight: "1.8" }}>
                At first it is maddening. You want things done yesterday. You want the paperwork sorted, the tradesman to show up when he said he would, the bureaucracy to move at something approaching a human pace. Then, about a month in, it clicks. This is not laziness. It is a quiet refusal to live in a permanent state of stress. Brașovians know how to work - but they also know when to stop. By Friday afternoon, <Link href="/entertainment-brasov-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>the terraces are full</Link>. They work to live, not the other way around.
              </p>
              <blockquote className="my-10 pl-6 border-l-4" style={{ borderLeftColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-stone-700 text-xl font-light italic leading-relaxed" style={{ fontFamily: "Playfair Display, serif" }}>
                  Brașov is not perfect. But it offers something increasingly rare: a place where you can build something real, let your kids play outside without anxiety, and feel like you are actually living - not just existing.
                </p>
              </blockquote>
              <p className="text-stone-700 font-semibold leading-relaxed" style={{ lineHeight: "1.8" }}>
                Do not take my word for it. Come for a month. We will talk after.
              </p>
            </div>

            <FeaturedDeveloper />

            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-stone-200">
              {["Brașov", "Expat life", "Romania", "Relocation", "City guide"].map((tag) => (
                <span key={tag} className="text-xs px-3 py-1.5 border border-stone-300 text-stone-500 hover:border-stone-500 hover:text-stone-700 transition-colors cursor-pointer">
                  {tag}
                </span>
              ))}
            </div>
          </main>

          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="sticky top-24 flex flex-col gap-6">
            <SidebarAd />
            <div className="p-5 border border-stone-200 bg-white">
              <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                About Brașov Gateway
              </h4>
              <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support - all in one place.
              </p>
              <a href="/#contact"
                className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                GET IN TOUCH
              </a>
            </div>
            <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
              <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                Stay in the loop
              </h4>
              <p className="text-stone-400 text-xs font-light leading-relaxed mb-4">
                No spam. One email per week with what is actually happening in Brașov.
              </p>
              <NewsletterInline />
            </div>
              {/* CTA card — Relocation support */}
              <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Relocation support</p>
                <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                  Moving to Brașov?
                </h4>
                <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                  We help expats find apartments, navigate bureaucracy, and settle in — without the stress.
                </p>
                <Link href="/contact">
                  <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                    BOOK A FREE CALL
                  </button>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-20 pt-12 border-t border-stone-200">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
            <span className="text-xs uppercase tracking-widest text-stone-400">More from the blog</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {RELATED.map((post, i) => (
              <Link key={i} href={post.href} className="group cursor-pointer no-underline block">
                <div className="overflow-hidden mb-4">
                  <img src={post.img} alt={post.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>{post.tag}</span>
                <h3 className="mt-2 text-base font-semibold text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>
                  {post.title}
                </h3>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                  READ MORE <ChevronRight size={11} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
    </>
  );
}
